import { getRace, getClass, getBackground, getSpell } from '@character-creator/data';
import type { CharacterDraft, ClassLevel } from '../stores/characterCreationStore';
import type { RuntimeState } from './characterExport';
import { migrateToLatest, needsMigration, CURRENT_SCHEMA_VERSION } from './characterMigrations';

export interface ImportResult {
  success: boolean;
  draft?: CharacterDraft;
  runtimeState?: RuntimeState;
  schemaVersion?: string;
  warnings: string[];
  errors: string[];
}

/**
 * Parse and validate character JSON for import.
 * Warns on unknown IDs but allows import (supports homebrew).
 */
export function parseCharacterJson(jsonString: string): ImportResult {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Parse JSON
  let data: unknown;
  try {
    data = JSON.parse(jsonString);
  } catch {
    return {
      success: false,
      warnings: [],
      errors: ['Invalid JSON format. Please check the file is valid JSON.'],
    };
  }

  // Detect format and extract draft
  let draft: unknown;
  let runtimeState: RuntimeState | undefined;
  let schemaVersion: string | undefined;

  if (data && typeof data === 'object') {
    // New format: { schemaVersion, exportedAt, draft, runtimeState? }
    if ('schemaVersion' in data && 'draft' in data) {
      schemaVersion = typeof data.schemaVersion === 'string' ? data.schemaVersion : undefined;
      draft = data.draft;

      // Extract runtime state if present
      if ('runtimeState' in data && data.runtimeState && typeof data.runtimeState === 'object') {
        runtimeState = data.runtimeState as RuntimeState;
      }

      // Apply migrations if needed
      if (schemaVersion && needsMigration(schemaVersion)) {
        const migrationResult = migrateToLatest(data, schemaVersion);
        warnings.push(...migrationResult.warnings);

        if (migrationResult.migrated) {
          // Use the migrated data
          draft = migrationResult.data.draft;
          runtimeState = migrationResult.data.runtimeState;
          schemaVersion = migrationResult.data.schemaVersion;
          warnings.push(
            `Character data migrated from version ${migrationResult.fromVersion} to ${migrationResult.toVersion}.`
          );
        }
      } else if (schemaVersion && schemaVersion !== CURRENT_SCHEMA_VERSION) {
        // Unknown newer version - warn but continue
        warnings.push(
          `Character was exported with schema version ${schemaVersion}, current version is ${CURRENT_SCHEMA_VERSION}. ` +
          'Some features may not import correctly.'
        );
      }
    }
    // Legacy format: { draft, benefits }
    else if ('draft' in data && 'benefits' in data) {
      warnings.push('Legacy export format detected. Benefits will be recalculated from draft data.');
      draft = data.draft;
    }
    // Legacy wrapped format: { draft } without benefits or schemaVersion
    else if ('draft' in data && data.draft && typeof data.draft === 'object') {
      warnings.push('Older export format detected (pre-versioning).');
      draft = data.draft;
    }
    // Direct CharacterDraft format (oldest)
    else {
      draft = data;
    }
  } else {
    draft = data;
  }

  // Validate it's an object
  if (!draft || typeof draft !== 'object') {
    return {
      success: false,
      warnings: [],
      errors: ['Invalid character data format.'],
    };
  }

  const d = draft as Record<string, unknown>;

  // Validate required fields
  if (typeof d.name !== 'string') {
    errors.push('Missing or invalid character name.');
  }

  // Validate characterLevel
  if (typeof d.characterLevel === 'number') {
    if (d.characterLevel < 1 || d.characterLevel > 20) {
      errors.push('Character level must be between 1 and 20.');
    }
  } else if (d.characterLevel !== undefined) {
    errors.push('Invalid character level format.');
  }

  // Validate abilityScores
  if (d.abilityScores && typeof d.abilityScores === 'object') {
    const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
    const scores = d.abilityScores as Record<string, unknown>;
    for (const ability of abilities) {
      const score = scores[ability];
      if (typeof score === 'number') {
        if (score < 1 || score > 30) {
          errors.push(`Ability score ${ability} must be between 1 and 30.`);
        }
      } else if (score !== undefined) {
        errors.push(`Invalid ${ability} score format.`);
      }
    }
  }

  // Check for unknown IDs (warn but don't block)
  if (typeof d.raceId === 'string' && d.raceId) {
    const race = getRace(d.raceId);
    if (!race) {
      warnings.push(`Unknown race "${d.raceId}" - may be homebrew content.`);
    }
  }

  if (typeof d.classId === 'string' && d.classId) {
    const cls = getClass(d.classId);
    if (!cls) {
      warnings.push(`Unknown class "${d.classId}" - may be homebrew content.`);
    }
  }

  if (typeof d.backgroundId === 'string' && d.backgroundId) {
    const bg = getBackground(d.backgroundId);
    if (!bg) {
      warnings.push(`Unknown background "${d.backgroundId}" - may be homebrew content.`);
    }
  }

  // Check spell IDs
  const checkSpells = (spellIds: unknown, label: string) => {
    if (Array.isArray(spellIds)) {
      for (const spellId of spellIds) {
        if (typeof spellId === 'string' && spellId) {
          const spell = getSpell(spellId);
          if (!spell) {
            warnings.push(`Unknown ${label} "${spellId}" - may be homebrew content.`);
          }
        }
      }
    }
  };

  checkSpells(d.selectedCantrips, 'cantrip');
  checkSpells(d.knownSpells, 'spell');
  checkSpells(d.preparedSpells, 'spell');

  // If we have critical errors, don't proceed
  if (errors.length > 0) {
    return { success: false, warnings, errors };
  }

  // Build the validated draft with defaults for missing fields
  const validatedDraft: CharacterDraft = {
    // Metadata (preserve if present, generate new if missing)
    id: typeof d.id === 'string' ? d.id : crypto.randomUUID(),
    createdAt: typeof d.createdAt === 'string' ? d.createdAt : new Date().toISOString(),
    updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : new Date().toISOString(),
    playerName: typeof d.playerName === 'string' ? d.playerName : undefined,
    campaignName: typeof d.campaignName === 'string' ? d.campaignName : undefined,

    // Ruleset
    rulesetId: typeof d.rulesetId === 'string' ? d.rulesetId : 'srd-5.1',

    // Basics
    name: typeof d.name === 'string' ? d.name : '',
    characterLevel: typeof d.characterLevel === 'number' ? d.characterLevel : 1,

    // Race
    raceId: typeof d.raceId === 'string' ? d.raceId : null,
    subraceId: typeof d.subraceId === 'string' ? d.subraceId : null,
    abilityBonusChoices: Array.isArray(d.abilityBonusChoices) ? d.abilityBonusChoices : [],
    languageChoices: Array.isArray(d.languageChoices) ? d.languageChoices : [],

    // Class
    classLevels: Array.isArray(d.classLevels) ? d.classLevels.map((cl: unknown): ClassLevel => {
      const c = cl as Record<string, unknown>;
      return {
        classId: typeof c.classId === 'string' ? c.classId : '',
        level: typeof c.level === 'number' ? c.level : 1,
        skillProficiencyChoices: Array.isArray(c.skillProficiencyChoices) ? c.skillProficiencyChoices : [],
        subclassId: typeof c.subclassId === 'string' ? c.subclassId : null,
        fightingStyleId: typeof c.fightingStyleId === 'string' ? c.fightingStyleId : null,
        expertiseSkills: Array.isArray(c.expertiseSkills) ? c.expertiseSkills : [],
        landCircleTerrainId: typeof c.landCircleTerrainId === 'string' ? c.landCircleTerrainId : null,
        pactBoonId: typeof c.pactBoonId === 'string' ? c.pactBoonId : null,
        favoredEnemies: Array.isArray(c.favoredEnemies) ? c.favoredEnemies : [],
        naturalExplorerTerrains: Array.isArray(c.naturalExplorerTerrains) ? c.naturalExplorerTerrains : [],
        metamagicChoices: Array.isArray(c.metamagicChoices) ? c.metamagicChoices : [],
        eldritchInvocations: Array.isArray(c.eldritchInvocations) ? c.eldritchInvocations : [],
        asiChoices: c.asiChoices && typeof c.asiChoices === 'object' ? (c.asiChoices as ClassLevel['asiChoices']) : {},
      };
    }) : [],
    confirmedCommoner: d.confirmedCommoner === true,
    classId: typeof d.classId === 'string' ? d.classId : null,
    skillProficiencyChoices: Array.isArray(d.skillProficiencyChoices) ? d.skillProficiencyChoices : [],

    // Abilities
    abilityScoreMethod: isAbilityScoreMethod(d.abilityScoreMethod) ? d.abilityScoreMethod : 'point-buy',
    abilityScores: isAbilityScores(d.abilityScores)
      ? d.abilityScores
      : { strength: 8, dexterity: 8, constitution: 8, intelligence: 8, wisdom: 8, charisma: 8 },

    // Background
    backgroundId: typeof d.backgroundId === 'string' ? d.backgroundId : null,
    confirmedNoBackground: d.confirmedNoBackground === true,

    // Equipment
    equipmentSelections: isEquipmentSelections(d.equipmentSelections) ? d.equipmentSelections : {},
    weaponChoiceResolutions: (d.weaponChoiceResolutions && typeof d.weaponChoiceResolutions === 'object' && !Array.isArray(d.weaponChoiceResolutions)) ? d.weaponChoiceResolutions as Record<string, string> : {},
    customEquipment: Array.isArray(d.customEquipment) ? d.customEquipment : [],
    currency: isCurrency(d.currency) ? d.currency : { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },

    // Attacks
    attacks: Array.isArray(d.attacks) ? d.attacks : [],

    // Spells
    selectedCantrips: Array.isArray(d.selectedCantrips) ? d.selectedCantrips : [],
    knownSpells: Array.isArray(d.knownSpells) ? d.knownSpells : [],
    preparedSpells: Array.isArray(d.preparedSpells) ? d.preparedSpells : [],
    selectedFamiliarId: typeof d.selectedFamiliarId === 'string' ? d.selectedFamiliarId : null,

    // Details
    alignment: isAlignment(d.alignment) ? d.alignment : 'true-neutral',
    personalityTraits: Array.isArray(d.personalityTraits) ? d.personalityTraits : [],
    ideals: Array.isArray(d.ideals) ? d.ideals : [],
    bonds: Array.isArray(d.bonds) ? d.bonds : [],
    flaws: Array.isArray(d.flaws) ? d.flaws : [],
    backstory: typeof d.backstory === 'string' ? d.backstory : '',
    appearance: isAppearance(d.appearance) ? d.appearance : {},
  };

  return {
    success: true,
    draft: validatedDraft,
    runtimeState,
    schemaVersion,
    warnings,
    errors: [],
  };
}

// Type guards
function isAbilityScoreMethod(val: unknown): val is 'point-buy' | 'standard-array' | 'manual' {
  return val === 'point-buy' || val === 'standard-array' || val === 'manual';
}

function isAbilityScores(
  val: unknown
): val is { strength: number; dexterity: number; constitution: number; intelligence: number; wisdom: number; charisma: number } {
  if (!val || typeof val !== 'object') return false;
  const obj = val as Record<string, unknown>;
  return (
    typeof obj.strength === 'number' &&
    typeof obj.dexterity === 'number' &&
    typeof obj.constitution === 'number' &&
    typeof obj.intelligence === 'number' &&
    typeof obj.wisdom === 'number' &&
    typeof obj.charisma === 'number'
  );
}

function isEquipmentSelections(val: unknown): val is Record<number, number> {
  if (!val || typeof val !== 'object') return false;
  const obj = val as Record<string, unknown>;
  return Object.entries(obj).every(
    ([key, value]) => !isNaN(Number(key)) && typeof value === 'number'
  );
}

function isCurrency(val: unknown): val is { cp: number; sp: number; ep: number; gp: number; pp: number } {
  if (!val || typeof val !== 'object') return false;
  const obj = val as Record<string, unknown>;
  return (
    typeof obj.cp === 'number' &&
    typeof obj.sp === 'number' &&
    typeof obj.ep === 'number' &&
    typeof obj.gp === 'number' &&
    typeof obj.pp === 'number'
  );
}

function isAlignment(val: unknown): val is CharacterDraft['alignment'] {
  const validAlignments = [
    'lawful-good',
    'neutral-good',
    'chaotic-good',
    'lawful-neutral',
    'true-neutral',
    'chaotic-neutral',
    'lawful-evil',
    'neutral-evil',
    'chaotic-evil',
    'unaligned',
  ];
  return typeof val === 'string' && validAlignments.includes(val);
}

function isAppearance(val: unknown): val is CharacterDraft['appearance'] {
  if (!val || typeof val !== 'object') return false;
  const obj = val as Record<string, unknown>;
  const validKeys = ['age', 'height', 'weight', 'eyes', 'skin', 'hair'];
  return Object.keys(obj).every(
    (key) => validKeys.includes(key) && (obj[key] === undefined || typeof obj[key] === 'string')
  );
}
